import React, { useState, useRef, useEffect, useCallback } from 'react';
import { UserPlus, CalendarPlus, ClipboardCheck, Users, CreditCard, FileText, Calendar, Search, Clock, User, Scissors, Download, ChevronLeft, ChevronRight, AlertCircle, Camera, Upload, BookOpen, ClipboardList, BookMarked, Share2, ArrowLeftRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSmartTranslation } from '../../hooks/useStudentLabel';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

/**
 * Enterprise Quick Actions Bar
 * Horizontal strip of the most-used daily action buttons.
 * Different actions per centerType.
 * Therapy centers also show today's meetings and surveys as quick-access items.
 */
export default function QuickActionsBar({
  centerType, onAddStudent, onAddLesson, onAddMeeting, onAddGroup,
  onRecordPayment, onAddSurvey, onSearchStudent, onScheduleTreatment,
  onUploadDocument,
  isMobile, isRTL, onExportPdf,
  surveys = [], todayMeetings = [], onSurveyClick, onEditMeeting,
  role = null,
  onRecordMemorization = null,
  onAssignHomework = null,
  onCollectGroups = null,
  // Today's lessons, already in hand on the caller's side. Passing them here
  // saves this bar from re-fetching the center's entire lesson history just to
  // find today's — see the attendance-nudge effect below.
  todayLessons = null,
  // Learning (non-sport, non-Quran) centers: schedule a 1-on-1 lesson.
  onAddPrivateLesson = null,
  // Learning centers, managers: the "move students between groups" flow.
  onMoveStudents = null,
}) {
  const { t } = useTranslation();
  // Sub-type aware labels (sport: "Add client" / "Schedule session").
  const smartT = useSmartTranslation();
  const navigate = useNavigate();
  // Hover state handled via inline style manipulation in onMouseEnter/onMouseLeave

  // Scroll fade state
  const scrollRef = useRef(null);
  const meetingsScrollRef = useRef(null);
  const [showStartFade, setShowStartFade] = useState(false);
  const [showEndFade, setShowEndFade] = useState(false);
  const [showMeetingsStartFade, setShowMeetingsStartFade] = useState(false);
  const [showMeetingsEndFade, setShowMeetingsEndFade] = useState(false);

  const updateFades = useCallback((el, setStart, setEnd) => {
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const effectiveScroll = isRTL ? -scrollLeft : scrollLeft;
    setStart(Math.abs(effectiveScroll) > 8);
    setEnd(Math.abs(effectiveScroll) + clientWidth < scrollWidth - 8);
  }, [isRTL]);

  const handleScroll = useCallback(() => {
    updateFades(scrollRef.current, setShowStartFade, setShowEndFade);
  }, [updateFades]);

  const handleMeetingsScroll = useCallback(() => {
    updateFades(meetingsScrollRef.current, setShowMeetingsStartFade, setShowMeetingsEndFade);
  }, [updateFades]);

  // Check fades on mount, when actions change, and on window resize
  useEffect(() => {
    handleScroll();
    handleMeetingsScroll();
    const onResize = () => { handleScroll(); handleMeetingsScroll(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [handleScroll, handleMeetingsScroll, surveys, todayMeetings]);

  const isTeacherRole = role === 'teacher';

  // Count today's lessons that still have no attendance recorded so we can
  // visually nudge the user from the dashboard. Learning centers only.
  const [unmarkedTodayCount, setUnmarkedTodayCount] = useState(0);
  useEffect(() => {
    if (centerType !== 'learning' || isTeacherRole) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // Prefer the caller's already-loaded list. Only centers whose
        // dashboard doesn't pass it fall back to fetching — and that fetch is
        // now capped, since an unbounded /lessons pulls a center's whole
        // history to answer a question about a single day.
        let todays = todayLessons;
        if (!Array.isArray(todays)) {
          const lessonsResp = await api.get('/lessons?limit=500');
          const all = Array.isArray(lessonsResp) ? lessonsResp : (lessonsResp?.data || []);
          const todayStr = new Date().toISOString().split('T')[0];
          todays = all.filter((l) => {
            const raw = l.scheduledDate;
            if (!raw) return false;
            const d = raw._seconds ? new Date(raw._seconds * 1000) : new Date(raw);
            return d.toISOString().split('T')[0] === todayStr;
          });
        }
        if (todays.length === 0) {
          if (!cancelled) setUnmarkedTodayCount(0);
          return;
        }
        const results = await Promise.all(
          todays.map((l) => api.get(`/attendance/lesson/${l.id}`).catch(() => []))
        );
        if (cancelled) return;
        let count = 0;
        results.forEach((r) => {
          const records = Array.isArray(r) ? r : (r?.data || []);
          const hasAny = records.some((rec) => rec.status && rec.status !== 'notYetJoined');
          if (!hasAny) count += 1;
        });
        setUnmarkedTodayCount(count);
      } catch {
        /* silent — dashboard non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, [centerType, isTeacherRole, todayLessons]);

  const hasAttendanceAlert = unmarkedTodayCount > 0;
  const attendanceLabel = hasAttendanceAlert
    ? t('dashboard.attendanceNeedsAction', 'Attendance · {{count}} pending', { count: unmarkedTodayCount })
    : t('dashboard.markAttendance', 'Attendance');

  const learningActions = [
    // Quran (Hifz) centers only — gated at the call site (props passed only when
    // isQuran), so non-Quran learning dashboards are unchanged. "Record
    // Memorization" merges the old Record-Recitation and Set-Memorized actions
    // (multiple surahs, per student or whole group).
    onRecordMemorization && { icon: BookMarked, label: t('quran.recordMemorization', 'Record Memorization'), color: '#0d9488', bg: 'rgba(13, 148, 136, 0.1)', action: onRecordMemorization },
    onAssignHomework && { icon: ClipboardList, label: t('quran.assignHomework', 'Assign Homework'), color: '#6366f1', bg: 'rgba(99, 102, 241, 0.1)', action: onAssignHomework },
    // Quran centers only — send the public onboarding link so a new center's
    // teachers fill in their own حلقات (groups + students) via a 48h form.
    onCollectGroups && { icon: Share2, label: t('onboarding.collectAction', 'Collect Groups'), color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.1)', action: onCollectGroups },
    onAddPrivateLesson && { icon: User, label: t('privateLessons.add', 'Private lesson'), color: '#0f766e', bg: 'rgba(15, 118, 110, 0.1)', action: onAddPrivateLesson },
    // Teachers don't see "Add Student" as a standalone — they use the
    // atomic new-student-lesson flow embedded in Create Lesson.
    !isTeacherRole && { icon: UserPlus, label: smartT('dashboard.addStudent', { defaultValue: 'Add Student' }), color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.1)', action: onAddStudent || (() => navigate('/students')) },
    { icon: CalendarPlus, label: smartT('dashboard.createLesson', { defaultValue: 'Create Lesson' }), color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', action: onAddLesson },
    {
      icon: ClipboardCheck,
      label: attendanceLabel,
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.1)',
      action: () => navigate(isTeacherRole ? '/teacher/attendance' : '/lessons'),
      needsAttention: !isTeacherRole && hasAttendanceAlert,
      badgeCount: unmarkedTodayCount,
      ariaLabel: hasAttendanceAlert
        ? t('dashboard.attendanceAriaPending', '{{count}} lessons today still need attendance marked', { count: unmarkedTodayCount })
        : undefined,
    },
    !isTeacherRole && { icon: Users, label: t('dashboard.createGroup', 'Create Group'), color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', action: onAddGroup || (() => navigate('/groups')) },
    !isTeacherRole && onMoveStudents && { icon: ArrowLeftRight, label: t('groupMove.flow.action', 'Move students between groups'), color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.1)', action: onMoveStudents, testId: 'quick-action-move-students' },
    !isTeacherRole && { icon: CreditCard, label: t('dashboard.recordPayment', 'Record Payment'), color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', action: onRecordPayment || (() => navigate('/jibaya')) },
  ].filter(Boolean);

  const therapyActions = [
    { icon: CalendarPlus, label: t('dashboard.newMeeting', 'New Meeting'), color: '#6366f1', bg: 'rgba(99, 102, 241, 0.1)', action: onAddMeeting },
    { icon: Search, label: t('dashboard.viewClient', 'View Client'), color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.1)', action: onSearchStudent || (() => navigate('/students')) },
    { icon: Calendar, label: t('dashboard.schedule', 'Schedule'), color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', action: () => navigate('/calendar') },
  ];

  const hairTransplantActions = [
    ...therapyActions,
    { icon: Scissors, label: t('scheduling.scheduleTreatment', 'Schedule Treatment'), color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', action: onScheduleTreatment || (() => {}) },
    { icon: Scissors, label: t('dashboard.patients', 'Patients'), color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', action: () => navigate('/patients') },
    { icon: Camera, label: t('dashboard.quickImageUpload', 'Quick Image Upload'), color: '#ec4899', bg: 'rgba(236, 72, 153, 0.1)', action: () => navigate('/transplant-images') },
    // Mobile-only "Upload document" wizard — thumb-friendly two-step flow
    // (pick client → camera or file) so on-site uploads don't require
    // navigating to the client detail page first.
    ...(isMobile && onUploadDocument
      ? [{ icon: Upload, label: t('dashboard.uploadDocument', 'Upload Document'), color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.1)', action: onUploadDocument }]
      : []),
    ...(onExportPdf ? [{ icon: Download, label: t('common.exportPdf', 'Export PDF'), color: 'var(--color-text-secondary, #6B7280)', bg: 'rgba(107, 114, 128, 0.1)', action: onExportPdf }] : []),
  ];

  const baseActions = centerType === 'hair_transplant' ? hairTransplantActions
    : centerType === 'therapy' ? therapyActions
    : learningActions;

  // Convert surveys into action buttons with the same design
  const surveyColors = ['#8b5cf6', '#6366f1', '#a855f7', '#7c3aed'];
  const surveyActions = surveys.map((survey, i) => ({
    icon: FileText,
    label: survey.title,
    color: surveyColors[i % surveyColors.length],
    bg: `${surveyColors[i % surveyColors.length]}1a`,
    action: () => onSurveyClick?.(survey),
    isSurvey: true,
  }));

  const actions = [...baseActions, ...surveyActions];

  const fadeStyle = (side) => ({
    position: 'absolute',
    top: 0,
    [side]: 0,
    width: '24px',
    height: '100%',
    background: `linear-gradient(${side === (isRTL ? 'right' : 'left') ? (isRTL ? 'to left' : 'to right') : (isRTL ? 'to right' : 'to left')}, var(--color-bg, #F8F9FA), transparent)`,
    pointerEvents: 'none',
    zIndex: 1,
    transition: 'opacity 0.2s ease',
  });

  const scrollBy = (el, delta) => {
    if (!el) return;
    const amount = isRTL ? -delta : delta;
    try {
      el.scrollBy({ left: amount, behavior: 'smooth' });
    } catch {
      el.scrollLeft += amount;
    }
  };

  const arrowButtonStyle = (side, visible) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: 0,
    zIndex: 2,
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: '1px solid var(--color-border, #e5e7eb)',
    backgroundColor: 'var(--color-surface, #fff)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    display: visible ? 'inline-flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-primary, #374151)',
    padding: 0,
    transition: 'opacity 0.2s ease, transform 0.15s ease',
  });

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <style>{`
        .qa-scroll::-webkit-scrollbar { display: none; }
        @keyframes qa-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.35); opacity: 0.75; }
        }
      `}</style>

      <h3 style={{
        fontSize: '0.75rem',
        fontWeight: '600',
        color: 'var(--color-text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: '0.625rem',
      }}>
        {t('dashboard.quickActions', 'Quick Actions')}
      </h3>

      {/* Action pills with scroll fades */}
      <div style={{ position: 'relative' }}>
        {/* Start fade */}
        <div style={{ ...fadeStyle(isRTL ? 'right' : 'left'), opacity: showStartFade ? 1 : 0 }} />
        {/* End fade */}
        <div style={{ ...fadeStyle(isRTL ? 'left' : 'right'), opacity: showEndFade ? 1 : 0 }} />

        {/* Scroll arrows (desktop/tablet; hidden on mobile where touch-scroll suffices) */}
        {!isMobile && (
          <>
            <button
              type="button"
              aria-label={t('common.scrollPrev', 'Scroll')}
              data-qa-scroll="prev"
              onClick={() => scrollBy(scrollRef.current, -240)}
              style={arrowButtonStyle(isRTL ? 'right' : 'left', showStartFade)}
            >
              {isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
            <button
              type="button"
              aria-label={t('common.scrollNext', 'Scroll')}
              data-qa-scroll="next"
              onClick={() => scrollBy(scrollRef.current, 240)}
              style={arrowButtonStyle(isRTL ? 'left' : 'right', showEndFade)}
            >
              {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            </button>
          </>
        )}

        <div
          ref={scrollRef}
          className="qa-scroll"
          role="toolbar"
          aria-label={t('dashboard.quickActions', 'Quick Actions')}
          onScroll={handleScroll}
          style={{
            display: 'flex',
            gap: '0.5rem',
            overflowX: 'auto',
            paddingBottom: '0.25rem',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {actions.map((action, idx) => {
            const isPrimary = idx === 0 && !action.isSurvey;
            const alert = action.needsAttention;
            return (
              <button
                key={action.isSurvey ? `survey-${idx}` : idx}
                onClick={action.action}
                data-testid={alert ? 'dashboard-attendance-alert' : action.testId}
                aria-label={action.ariaLabel}
                onMouseEnter={(e) => {
                  if (alert) {
                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.14)';
                    e.currentTarget.style.borderColor = '#EF4444';
                    return;
                  }
                  if (isPrimary) {
                    e.currentTarget.style.filter = 'brightness(0.92)';
                  } else {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
                    e.currentTarget.style.borderColor = 'var(--color-primary-dark, #355760)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (alert) {
                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)';
                    e.currentTarget.style.borderColor = '#EF4444';
                    return;
                  }
                  if (isPrimary) {
                    e.currentTarget.style.filter = 'brightness(1)';
                  } else {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                  }
                }}
                title={action.isSurvey ? action.label : undefined}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: isMobile ? '0.5rem 0.875rem' : '0.5625rem 1rem',
                  borderRadius: '9999px',
                  border: alert
                    ? '1.5px solid #EF4444'
                    : isPrimary ? '1px solid transparent' : '1px solid var(--color-border)',
                  backgroundColor: alert
                    ? 'rgba(239, 68, 68, 0.08)'
                    : isPrimary ? 'var(--color-primary-dark, #355760)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease, border-color 150ms ease, filter 150ms ease',
                  maxWidth: action.isSurvey ? '180px' : undefined,
                  scrollSnapAlign: 'start',
                  flexShrink: 0,
                  minHeight: isMobile ? '40px' : '38px',
                  boxShadow: alert
                    ? '0 0 0 4px rgba(239, 68, 68, 0.12)'
                    : isPrimary ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  position: 'relative',
                }}
              >
                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                  <action.icon
                    size={16}
                    color={alert ? '#B91C1C' : isPrimary ? 'var(--color-on-primary, #fff)' : action.color}
                    style={{ flexShrink: 0 }}
                  />
                  {alert && (
                    <span
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        top: '-4px',
                        insetInlineEnd: '-4px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#EF4444',
                        boxShadow: '0 0 0 2px var(--color-surface, #fff)',
                        animation: 'qa-pulse 1.6s ease-in-out infinite',
                      }}
                    />
                  )}
                </span>
                <span style={{
                  fontSize: '0.8125rem',
                  fontWeight: alert ? 600 : isPrimary ? 600 : 500,
                  color: alert ? 'var(--color-danger-dark, #B91C1C)' : isPrimary ? 'var(--color-on-primary, #fff)' : 'var(--color-text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: action.isSurvey ? 'hidden' : undefined,
                  textOverflow: action.isSurvey ? 'ellipsis' : undefined,
                  maxWidth: action.isSurvey ? '140px' : undefined,
                }}>
                  {action.label}
                </span>
                {alert && (
                  <AlertCircle size={14} color="#B91C1C" style={{ flexShrink: 0 }} aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Today's Meetings section (therapy only) */}
      {(centerType === 'therapy' || centerType === 'hair_transplant') && todayMeetings.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <h4 style={{
            fontSize: '0.8125rem',
            fontWeight: '600',
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.375rem',
          }}>
            {t('dashboard.todayMeetings', "Today's Meetings")}
          </h4>
          <div style={{ position: 'relative' }}>
            <div style={{ ...fadeStyle(isRTL ? 'right' : 'left'), opacity: showMeetingsStartFade ? 1 : 0 }} />
            <div style={{ ...fadeStyle(isRTL ? 'left' : 'right'), opacity: showMeetingsEndFade ? 1 : 0 }} />
            {!isMobile && (
              <>
                <button
                  type="button"
                  aria-label={t('common.scrollPrev', 'Scroll')}
                  data-qa-scroll="meetings-prev"
                  onClick={() => scrollBy(meetingsScrollRef.current, -240)}
                  style={arrowButtonStyle(isRTL ? 'right' : 'left', showMeetingsStartFade)}
                >
                  {isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                </button>
                <button
                  type="button"
                  aria-label={t('common.scrollNext', 'Scroll')}
                  data-qa-scroll="meetings-next"
                  onClick={() => scrollBy(meetingsScrollRef.current, 240)}
                  style={arrowButtonStyle(isRTL ? 'left' : 'right', showMeetingsEndFade)}
                >
                  {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                </button>
              </>
            )}
            <div
              ref={meetingsScrollRef}
              className="qa-scroll"
              onScroll={handleMeetingsScroll}
              style={{
                display: 'flex',
                gap: '0.5rem',
                overflowX: 'auto',
                paddingBottom: '0.25rem',
                scrollSnapType: 'x mandatory',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {todayMeetings.map((meeting) => (
                <button
                  key={meeting.id}
                  onClick={() => onEditMeeting?.(meeting)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = 'brightness(0.92)';
                    e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.14)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = 'brightness(1)';
                    e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.08)';
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: isMobile ? '0.375rem 0.625rem' : '0.4375rem 0.75rem',
                    borderRadius: '9999px',
                    border: 'none',
                    backgroundColor: 'rgba(99, 102, 241, 0.08)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    scrollSnapAlign: 'start',
                    flexShrink: 0,
                    minHeight: isMobile ? '36px' : undefined,
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    color: '#6366f1',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                  }}>
                    <Clock size={14} />
                    <span>{meeting.time || '--:--'}</span>
                  </div>
                  <span style={{
                    width: '1px',
                    height: '14px',
                    backgroundColor: 'rgba(99, 102, 241, 0.3)',
                    flexShrink: 0,
                  }} />
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}>
                    <User size={14} color="#6366f1" />
                    <span style={{
                      fontSize: '0.8125rem',
                      fontWeight: '500',
                      color: '#6366f1',
                      whiteSpace: 'nowrap',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {meeting.studentName || t('dashboard.meeting', 'Meeting')}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
